import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { eventsApi, PublicEvent } from '../api/client';
import { CalendarIcon, MapPinIcon, TicketIcon, CalendarDaysIcon, UserGroupIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Logo from '../components/Logo';
import { formatEventDateTimeCompact } from '../utils/datetime';

const HomePage: React.FC = () => {
  const [events, setEvents] = useState<PublicEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past'>('upcoming');
  const [showAttendeesModal, setShowAttendeesModal] = useState(false);
  const [selectedEventAttendees, setSelectedEventAttendees] = useState<{ eventTitle: string; show_list: boolean; total: number; attendees: Array<{ attendee_type: string; job_title: string }> } | null>(null);
  const [loadingAttendees, setLoadingAttendees] = useState(false);

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      const data = await eventsApi.getPublicEvents();
      // Sort events by date
      const sortedEvents = data.sort((a, b) => 
        new Date(a.date_time).getTime() - new Date(b.date_time).getTime()
      );
      setEvents(sortedEvents);
    } catch (error) {
      console.error('Failed to load events:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAttendees = async (event: PublicEvent) => {
    setLoadingAttendees(true);
    try {
      const data = await eventsApi.getPublicAttendees(event.id);
      setSelectedEventAttendees({
        eventTitle: event.title,
        ...data
      });
      setShowAttendeesModal(true);
    } catch (error) {
      console.error('Failed to load attendees:', error);
    } finally {
      setLoadingAttendees(false);
    }
  };

  // Filter events based on whether they're in the past or future
  const now = new Date();
  const upcomingEvents = events.filter(event => new Date(event.date_time) >= now);
  const pastEvents = events.filter(event => new Date(event.date_time) < now);
  const displayedEvents = activeTab === 'upcoming' ? upcomingEvents : pastEvents;

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <Logo size="xlarge" />
            <nav className="flex space-x-4">
              <Link
                to="/login"
                className="text-gray-600 hover:text-gray-900 font-medium"
              >
                Organizer Login
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Events</h1>
        
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('upcoming')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'upcoming'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Upcoming Events
              {upcomingEvents.length > 0 && (
                <span className="ml-2 bg-blue-100 text-blue-600 py-0.5 px-2.5 rounded-full text-xs">
                  {upcomingEvents.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('past')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'past'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Past Events
              {pastEvents.length > 0 && (
                <span className="ml-2 bg-gray-100 text-gray-600 py-0.5 px-2.5 rounded-full text-xs">
                  {pastEvents.length}
                </span>
              )}
            </button>
          </nav>
        </div>
      </div>

      {displayedEvents.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500">
            {activeTab === 'upcoming' 
              ? 'No upcoming events at the moment.' 
              : 'No past events to display.'}
          </p>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {displayedEvents.map((event) => (
            <Link
              key={event.id}
              to={`/event/${event.id}`}
              className="block bg-white rounded-lg shadow hover:shadow-lg transition-shadow"
            >
              <div className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  {event.title}
                </h2>
                
                {event.description && (
                  <p className="text-gray-600 mb-4 line-clamp-2">
                    {event.description}
                  </p>
                )}

                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    {formatEventDateTimeCompact(event.date_time, event.timezone || 'Europe/London')}
                  </div>
                  
                  <a 
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.venue)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center text-gray-600 hover:text-blue-600 transition-colors"
                  >
                    <MapPinIcon className="h-4 w-4 mr-2" />
                    {event.venue}
                  </a>
                  
                  {activeTab === 'upcoming' && (
                    <div className="flex items-center">
                      <TicketIcon className="h-4 w-4 mr-2" />
                      {event.registration_state === 'review' ? (
                        <span className="text-blue-600">By invitation — requests welcome</span>
                      ) : event.registration_state === 'waitlist' ? (
                        <span className="text-amber-600">Waitlist open</span>
                      ) : event.registration_state === 'closed' ? (
                        <span className="text-gray-500">Registration closed</span>
                      ) : (
                        <span className="text-green-600">
                          {event.available_spots} / {event.max_attendees} spots available
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div className="mt-4 flex items-center justify-between">
                  <div>
                    {activeTab === 'upcoming' ? (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                        Request Ticket →
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-600">
                        Event Ended
                      </span>
                    )}
                  </div>
                  {activeTab === 'upcoming' && (
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          loadAttendees(event);
                        }}
                        className="inline-flex items-center text-gray-600 hover:text-gray-900"
                        title="View attendees"
                        disabled={loadingAttendees}
                      >
                        <UserGroupIcon className="h-5 w-5" />
                      </button>
                      <a
                        href={`/api/events/public/${event.id}/calendar`}
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center text-gray-600 hover:text-gray-900"
                        title="Add to calendar"
                      >
                        <CalendarDaysIcon className="h-5 w-5" />
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
      </div>
      
      {/* Attendees Modal */}
      {showAttendeesModal && selectedEventAttendees && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-xl font-semibold">Attendees</h3>
                  <p className="text-gray-600 mt-1">{selectedEventAttendees.eventTitle}</p>
                  {selectedEventAttendees.show_list && (
                    <p className="text-sm text-gray-500 mt-2">{selectedEventAttendees.total} confirmed attendees</p>
                  )}
                </div>
                <button
                  onClick={() => {
                    setShowAttendeesModal(false);
                    setSelectedEventAttendees(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {!selectedEventAttendees.show_list ? (
                <p className="text-gray-500 text-center py-8">List coming soon</p>
              ) : selectedEventAttendees.attendees.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No attendees yet</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedEventAttendees.attendees.map((attendee, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-4">
                      <p className="font-medium text-gray-900">{attendee.attendee_type}</p>
                      <p className="text-gray-600">{attendee.job_title}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;